// src/components/dashboard/FinalizeView.tsx
import { useEffect, useRef, useState } from 'react';
import type { BookingJob } from '@/lib/types/workflow';
import { formatRand } from '@/lib/utils/currency';
import { formatSAST } from '@/lib/utils/date';
import { ConfidenceBar } from '@/components/ui/ConfidenceBar';

type Props = {
  booking: BookingJob;
  draft: string;
  onDraftChange: (next: string) => void;
  readOnly?: boolean;
};

export function FinalizeView({
  booking,
  draft,
  onDraftChange,
  readOnly = false,
}: Props) {
  // This used to show a fake "Auto-saving…" / "Saved {time}" indicator
  // driven purely by a local setTimeout — no draft-service call ever
  // happened, so an edit made here was silently discarded on send (see
  // DashboardPage.tsx::finalizeAndSend, which now actually persists the
  // edit before sending — [[finding-finalize-draft-edit-discarded-on-send]]).
  // Rather than re-introduce a debounced autosave (a real round trip on
  // every keystroke, inconsistent with how every other stage in this app
  // persists a draft — on an explicit action, not continuously), this
  // just stops claiming a save that hasn't happened: "Edited" while the
  // operator has changed the text since this screen loaded, nothing
  // otherwise. The actual, real save happens explicitly on Finalize & Send.
  const [dirty, setDirty] = useState(false);
  const initialDraft = useRef(draft);

  useEffect(() => {
    if (readOnly) return;
    setDirty(draft !== initialDraft.current);
  }, [draft, readOnly]);

  return (
    <div
      className="workflow-grid"
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: '16px',

        height: '100%',
        minHeight: 0,
        overflow: 'hidden',
      }}
    >

      {/* ================= LEFT ================= */}
      <section className="noir-panel h-full flex flex-col min-h-0 overflow-hidden">
        <div className="panel-kicker flex-shrink-0">
          <span className="mono-label">Original Request</span>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto pr-2">
          <div className="glass-card gradient-border" style={{ padding: 22 }}>
            <div className="font-mono" style={{ color: 'rgba(var(--fg-rgb),0.4)', fontSize: 11 }}>
              {booking.clientEmail} · {formatSAST(booking.receivedAt)}
            </div>

            <div
              className="font-serif"
              style={{
                color: 'var(--text-primary)',
                fontSize: 14,
                lineHeight: 1.55,
                whiteSpace: 'pre-wrap',
                marginTop: 10,
              }}
            >
              {booking.emailBody}
            </div>
          </div>

          <div className="glass-card gradient-border" style={{ padding: 22, marginTop: 16 }}>
            <ConfidenceBar value={booking.aiConfidence} />

            <div
              className="mt-4 pt-4 grid grid-cols-2 gap-3"
              style={{ borderTop: '1px solid var(--hairline)' }}
            >
              <Meta label="Dates" value={booking.rentalDates} />
              <Meta label="Location" value={booking.location} />
            </div>
          </div>
        </div>
      </section>

      {/* ================= MIDDLE (REVERTED LINE ITEMS) ================= */}
      <section className="noir-panel h-full flex flex-col min-h-0 overflow-hidden">
        <div className="panel-kicker flex-shrink-0">
          <span className="mono-label">Final Order</span>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto">
          <div className="glass-card gradient-border" style={{ padding: 22 }}>
            <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
              {booking.gear.map((g) => (
                <li
                  key={g.id}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    padding: '12px 0',
                    borderBottom: '1px solid var(--hairline)',
                  }}
                >
                  <div>
                    <div style={{ fontSize: 13.5, color: 'var(--text-primary)' }}>
                      {g.name}
                    </div>
                    <div className="mono-label">
                      QTY {g.quantity} · {formatRand(g.unitPrice)}/day
                    </div>
                  </div>

                  <div className="font-mono">
                    {formatRand(g.quantity * g.unitPrice)}
                  </div>
                </li>
              ))}
            </ul>

            {/* pricing */}
            <div
              style={{
                marginTop: 16,
                paddingTop: 16,
                borderTop: '1px solid var(--hairline-strong)',
              }}
            >
              <PricingRow label="Subtotal" value={booking.quoteTotalRands} />
              <PricingRow label="VAT (15%)" value={booking.quoteTotalRands * 0.15} muted />

              <div
                style={{
                  marginTop: 12,
                  paddingTop: 12,
                  borderTop: '1px solid var(--hairline)',
                  display: 'flex',
                  justifyContent: 'space-between',
                }}
              >
                <span className="font-serif" style={{ fontWeight: 600 }}>
                  Total (incl. VAT)
                </span>

                <span className="font-serif font-mono" style={{ fontSize: 18 }}>
                  {formatRand(booking.quoteTotalRands * 1.15)}
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ================= RIGHT ================= */}
      <section className="noir-panel h-full flex flex-col min-h-0 overflow-hidden">
        <div className="panel-kicker flex-shrink-0">
          <span className="mono-label">Outbound Email</span>

          <span
            className="mono-label"
            style={{
              marginLeft: 10,
              color: dirty ? 'var(--accent)' : 'rgba(var(--fg-rgb),0.4)',
            }}
          >
            {readOnly ? 'Sent' : dirty ? 'Edited — saves when you send' : 'Ready'}
          </span>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto">
          <div className="glass-card gradient-border" style={{ padding: 22 }}>
            <Field label="To" value={booking.clientEmail} />
            <Field
              label="Subject"
              value={booking.draftSubject || `RE: ${booking.subject}`}
            />

            <div style={{ marginTop: 12 }}>
              {readOnly ? (
                <div style={{ fontSize: 14, lineHeight: 1.6 }}>
                  {draft.split(/\n\n+/).map((p, i) => (
                    <p key={i} style={{ marginBottom: 10 }}>
                      {p}
                    </p>
                  ))}
                </div>
              ) : (
                <textarea
                  value={draft}
                  onChange={(e) => onDraftChange(e.target.value)}
                  rows={14}
                  style={{
                    width: '100%',
                    fontSize: 14,
                    lineHeight: 1.6,
                    color: 'var(--text-primary)',
                    background: 'transparent',
                    border: 'none',
                    outline: 'none',
                    resize: 'none',
                  }}
                />
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

/* helpers unchanged */

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ display: 'flex', gap: 10, padding: '8px 0' }}>
      <span className="mono-label" style={{ width: 60 }}>
        {label}
      </span>
      <span style={{ fontSize: 13 }}>{value}</span>
    </div>
  );
}

function Meta({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="mono-label">{label}</div>
      <div style={{ fontSize: 12.5 }}>{value}</div>
    </div>
  );
}

function PricingRow({
  label,
  value,
  muted,
}: {
  label: string;
  value: number;
  muted?: boolean;
}) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0' }}>
      <span className="mono-label">{label}</span>
      <span style={{ opacity: muted ? 0.5 : 1 }}>{formatRand(value)}</span>
    </div>
  );
}